// iOS (Safari and the WKWebView the mobile app runs in) ignores HTMLMediaElement.volume: the property is read-only
// and always reads 1, only `muted` works. Volume there has to go through the Web Audio API instead: the element is
// routed into a GainNode and the gain is what changes. Everywhere else the element's own volume is used, unchanged.

let volumeSupported: boolean | undefined
let sharedContext: AudioContext | undefined

// Whether setting HTMLMediaElement.volume actually does something on this platform
export function isMediaVolumeSupported(): boolean {
	if (volumeSupported === undefined) {
		if (typeof Audio === 'undefined') return true
		const probe = new Audio()
		probe.volume = 0.5
		volumeSupported = probe.volume === 0.5
	}
	return volumeSupported
}

function getSharedAudioContext(): AudioContext | undefined {
	if (sharedContext || typeof window === 'undefined' || typeof AudioContext === 'undefined') return sharedContext

	sharedContext = new AudioContext()
	// Keep playing through the silent switch, like plain <audio> elements did before being routed through Web Audio
	// (navigator.audioSession exists from iOS 16.4)
	const audioSession = (navigator as Navigator & { audioSession?: { type: string } }).audioSession
	if (audioSession) audioSession.type = 'playback'

	// A context starts (or, after an interruption like a phone call or backgrounding, ends up) suspended until a user
	// gesture resumes it; anything routed through it is silent meanwhile
	const resume = () => resumeSharedAudioContext()
	document.addEventListener('touchend', resume, { passive: true })
	document.addEventListener('click', resume)
	document.addEventListener('visibilitychange', () => {
		if (!document.hidden) resume()
	})
	return sharedContext
}

export function resumeSharedAudioContext() {
	if (sharedContext && sharedContext.state !== 'running' && sharedContext.state !== 'closed') {
		sharedContext.resume().catch(() => void 0)
	}
}

// Volume control for an <audio> element that also works on iOS. Use `volume` instead of `audio.volume`, and call
// dispose() once the element is dropped.
export class AudioVolume {
	private source?: MediaElementAudioSourceNode
	private gain?: GainNode
	private _volume: number

	constructor(
		private readonly audio: HTMLAudioElement,
		volume = audio.volume,
	) {
		this._volume = volume
		const context = isMediaVolumeSupported() ? undefined : getSharedAudioContext()
		if (context) {
			this.source = context.createMediaElementSource(audio)
			this.gain = context.createGain()
			this.source.connect(this.gain).connect(context.destination)
		}
		this.volume = volume
	}

	get volume(): number {
		return this._volume
	}

	set volume(value: number) {
		this._volume = Math.min(1, Math.max(0, value))
		if (this.gain) this.gain.gain.value = this._volume
		else this.audio.volume = this._volume
	}

	dispose() {
		this.source?.disconnect()
		this.gain?.disconnect()
		this.source = undefined
		this.gain = undefined
	}
}
